import { useState, useEffect } from 'react'
import { Loader2, Eye, Settings, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import deviceService, { Device } from '../services/deviceService'

export default function DeviceList() {
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null)
  const [deleting, setDeleting] = useState(false)

  const loadDevices = async () => {
    try {
      setLoading(true)
      setError(null)
      const deviceList = await deviceService.getDevices()
      setDevices(deviceList)
    } catch (err) {
      console.error('Error loading devices:', err)
      setError('Failed to load devices. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDevices()
  }, [])

  const handleViewDevice = (device: Device) => {
    setSelectedDevice(device)
    setDeviceDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDeviceDialogOpen(false)
    setSelectedDevice(null)
  }

  const handleDeleteDevice = (device: Device) => {
    setDeviceToDelete(device)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setDeviceToDelete(null)
  }

  const handleConfirmDelete = async () => {
    if (!deviceToDelete) return

    try {
      setDeleting(true)
      await deviceService.deleteDevice(deviceToDelete.deviceId)
      
      setDevices(prev => prev.filter(d => d.id !== deviceToDelete.id))
      setError(null)
      setDeleteDialogOpen(false)
      setDeviceToDelete(null)
      
      console.log(`Device ${deviceToDelete.deviceName} deleted successfully. Registration code released for reuse.`)
    } catch (err) {
      console.error('Error deleting device:', err)
      setError('Failed to delete device. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleString()
  }

  const getStatusVariant = (status?: 'online' | 'offline' | 'error') => {
    switch (status) {
      case 'online':
        return 'default'
      case 'offline':
        return 'secondary'
      case 'error':
        return 'destructive'
      default:
        return 'secondary'
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold">Devices</h1>
        <Button
          variant="outline"
          onClick={loadDevices}
          disabled={loading}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Device Name</TableHead>
              <TableHead>Device ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Seen</TableHead>
              <TableHead>Registered</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <p className="text-sm text-muted-foreground">
                    No devices registered yet. Generate a registration code to add your first device.
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device) => (
                <TableRow key={device.id}>
                  <TableCell className="font-medium">{device.deviceName}</TableCell>
                  <TableCell>
                    <code className="text-sm">{device.deviceId}</code>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(device.status)}>
                      {device.status || 'offline'}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(device.lastSeen)}</TableCell>
                  <TableCell>{formatDate(device.createdAt)}</TableCell>
                  <TableCell>{device.location || '-'}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDevice(device)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      color="red"
                      size="sm"
                      onClick={() => handleDeleteDevice(device)}
                    >
                      <Settings className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Device Details Dialog */}
      <Dialog open={deviceDialogOpen} onOpenChange={setDeviceDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Device Details</DialogTitle>
          </DialogHeader>
          {selectedDevice && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Device Name</Label>
                <Input value={selectedDevice.deviceName} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Device ID</Label>
                <Input value={selectedDevice.deviceId} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Input value={selectedDevice.status || 'offline'} readOnly />
              </div>
              <div className="space-y-2">
                <Label>IP Address</Label>
                <Input value={selectedDevice.ipAddress || 'Unknown'} readOnly />
              </div>
              <div className="space-y-2">
                <Label>MAC Address</Label>
                <Input value={selectedDevice.macAddress || 'Unknown'} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Version</Label>
                <Input value={selectedDevice.version || 'Unknown'} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={selectedDevice.location || 'Not set'} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Last Seen</Label>
                <Input value={formatDate(selectedDevice.lastSeen)} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Registered At</Label>
                <Input value={formatDate(selectedDevice.createdAt)} readOnly />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleCloseDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Device</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete device "{deviceToDelete?.deviceName}"?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-2">This will:</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>Remove the device from your account</li>
              <li>Release the registration code for reuse</li>
              <li>Revoke its AWS IoT certificates</li>
              <li>Delete all associated data</li>
            </ul>
            <p className="text-sm text-destructive mt-4">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseDeleteDialog}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Device'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
